import { zuJson, type VideoOverlay } from '../../domain/videoAnnotation';
import { oeffneErneut } from '../../persistence/anhaenge';
import { getStore } from '../../persistence/store';
import { annotationsPfad, deleteFileMitSignatur, saveTextAs } from '../../platform/storage';
import { currentFileSubject } from '../../store/useAppStore';

/**
 * Sichert die zeitlich begrenzte Markierungsebene einer Videoaufnahme.
 *
 * Herausgelöst aus dem Editor, damit sich der Entscheid „schreiben, überschreiben, löschen oder
 * nichts tun" ohne React prüfen lässt — dasselbe Vorgehen wie bei `annotateSave.ts` und
 * `handwritingSave.ts`.
 */

/**
 * Inhaltlicher Vergleich zweier Ebenen — keine Objektgleichheit: die Bearbeitung legt bei jeder
 * Änderung neue Objekte an, ein Referenzvergleich hielte deshalb jedes blosse Ansehen für eine
 * Änderung.
 *
 * `null` (es gab keine Datei) und eine leere Ebene (es gab eine, sie wurde geräumt) sind
 * ausdrücklich **nicht** dasselbe: nur der zweite Fall löst ein Löschen aus.
 */
export function ebeneGleich(a: VideoOverlay | null, b: VideoOverlay | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * `vorher` ist der Stand, mit dem der Prüfer die Ebene vorgefunden hat (`null`, wenn es keine
 * Datei gab), `nachher` der beim Schliessen.
 *
 * Drei Ausgänge — dieselben wie bei der Markierebene eines Fotos:
 * - **unverändert:** nichts geschieht. Ohne diese Wache legte jedes blosse Öffnen und
 *   Schliessen eine neue Datei an, eine neue Beglaubigung und einen neuen Übertragungsauftrag.
 * - **leer geräumt:** Datei **und** Beglaubigung werden gelöscht. Eine Ebene ohne Blöcke zu
 *   schreiben hiesse, eine leere Datei mit in die Übertragung zu geben.
 * - **geändert:** dieselbe Datei wird überschrieben und neu beglaubigt.
 *
 * ⚠️ **Eine Protokollzeile entsteht in keinem der drei Fälle.** Der `value` der Medienzeilen
 * trägt die **Anzahl** der Aufnahmen (`domain/medienzahl.ts`), und die Ebene gehört zu ihrem
 * Video: sie zählt nicht extra. Ihre Zeile trüge nur die unveränderte Videoanzahl — und
 * verdrängte dabei den jüngsten Eintrag, den der Server als einzigen auswertet.
 *
 * Die **Datei** geht trotzdem hinaus: `server/anhaenge.ts` führt sie als eigene Art und findet
 * sie über das Dateisystem, nicht über das Protokoll.
 */
export async function sichereVideoEbene(
  videoPfad: string,
  taskId: string,
  vorher: VideoOverlay | null,
  nachher: VideoOverlay,
): Promise<void> {
  if (ebeneGleich(vorher, nachher)) return;

  const ziel = annotationsPfad(videoPfad);

  if (nachher.blocks.length === 0) {
    // Gab es vorher gar keine Datei, ist auch nichts zu löschen — `deleteFileMitSignatur`
    // schluckt „nicht gefunden", der Aufruf bliebe also folgenlos, aber unnötig.
    if (vorher !== null) {
      await deleteFileMitSignatur(ziel);
      // Die Zeile über die Übertragung zeigte sonst auf eine Datei, die es nicht mehr gibt.
      await oeffneErneut(getStore(), ziel);
    }
    return;
  }

  await saveTextAs(ziel, zuJson(nachher), { ...currentFileSubject(), taskId });

  /*
   * ⚠️ **Die Ebene behält ihren Namen, also auch ihren Übertragungsstand.** War sie im
   * Relay-Modus schon hinausgegangen, stünde `erledigt='Y'` daneben und die geänderte Fassung
   * ginge nie wieder hinaus — am Gerät die neue Markierung, in der Auswertung die alte
   * (`persistence/anhaenge.ts::oeffneErneut`).
   */
  await oeffneErneut(getStore(), ziel);
}
